/**
 * ProviderClientsTab Component
 * 
 * Displays the provider's active client list from Healthie EHR
 * Uses Material-UI Table with custom pagination for in-memory data
 */
import React, { useState, useMemo } from 'react';
import { useRecordContext } from 'react-admin';
import {
    Box,
    Typography,
    Alert,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TableSortLabel,
    Paper
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';

const ProviderClientsTab = () => {
    const record = useRecordContext();
    const healthieData = record?.healthie_data;
    const patients = healthieData?.patients || [];
    const totalPatientCount = patients.length;

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [orderBy, setOrderBy] = useState('next_appt_date');
    const [order, setOrder] = useState('asc');

    // Sorting logic
    const sortedPatients = useMemo(() => {
        const sorted = [...patients].sort((a, b) => {
            let aVal = a[orderBy];
            let bVal = b[orderBy];

            // Handle null/undefined values
            if (!aVal && !bVal) return 0;
            if (!aVal) return 1;
            if (!bVal) return -1;

            // Convert dates for comparison
            if (orderBy === 'next_appt_date') {
                aVal = new Date(aVal);
                bVal = new Date(bVal);
            }

            if (aVal < bVal) return order === 'asc' ? -1 : 1;
            if (aVal > bVal) return order === 'asc' ? 1 : -1;
            return 0;
        });

        return sorted;
    }, [patients, orderBy, order]);

    // Paginated data
    const paginatedPatients = useMemo(() => {
        const start = page * rowsPerPage;
        return sortedPatients.slice(start, start + rowsPerPage);
    }, [sortedPatients, page, rowsPerPage]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    if (patients.length === 0) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="info" icon={<InfoOutlinedIcon />}>
                    <Typography variant="body2">
                        This provider has no active clients in Healthie EHR.
                    </Typography>
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <PersonIcon color="primary" />
                <Typography variant="h6">
                    Active Clients ({totalPatientCount.toLocaleString()})
                </Typography>
                {totalPatientCount > patients.length && (
                    <Chip
                        label={`Showing first ${patients.length}`}
                        size="small"
                        color="info"
                        variant="outlined"
                    />
                )}
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <TableSortLabel
                                    active={orderBy === 'full_name'}
                                    direction={orderBy === 'full_name' ? order : 'asc'}
                                    onClick={() => handleRequestSort('full_name')}
                                >
                                    Client Name
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={orderBy === 'email'}
                                    direction={orderBy === 'email' ? order : 'asc'}
                                    onClick={() => handleRequestSort('email')}
                                >
                                    Email
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={orderBy === 'next_appt_date'}
                                    direction={orderBy === 'next_appt_date' ? order : 'asc'}
                                    onClick={() => handleRequestSort('next_appt_date')}
                                >
                                    Next Appointment
                                </TableSortLabel>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedPatients.map((patient) => (
                            <TableRow key={patient.id} hover>
                                <TableCell>
                                    <Typography variant="body2" fontWeight={500}>
                                        {patient.full_name || 'Unknown Client'}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" color="text.secondary">
                                        {patient.email || 'N/A'}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    {patient.next_appt_date ? (
                                        <Chip
                                            icon={<EventIcon />}
                                            label={new Date(patient.next_appt_date).toLocaleDateString()}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                    ) : (
                                        <Typography variant="caption" color="text.secondary">
                                            No upcoming appointment
                                        </Typography>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={patients.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </TableContainer>

                {totalPatientCount > 100 && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                            <strong>Large client list detected ({totalPatientCount.toLocaleString()} clients).</strong>
                        </Typography>
                        <Typography variant="caption" display="block">
                            This data is cached for 10 minutes for optimal performance. The table below supports sorting and pagination for easier navigation.
                        </Typography>
                    </Alert>
                )}
        </Box>
    );
};

export default ProviderClientsTab;

